"use client";

import { useCallback, useRef } from "react";
import { toast } from "sonner";
import { useCart } from "@/stores/cart-store";
import type { CartValidateResponse } from "@/lib/cart-validate-contract";

/**
 * Hook to validate cart items against the server and sync (remove
 * invalid items, refresh stale category snapshots). Use when opening
 * the cart sheet or loading checkout.
 */
export function useCartValidation() {
  // One atomic selector per value. A bare `useCart()` subscribes to the whole
  // store, which every `set()` replaces — so this hook (mounted by CartSheet
  // AND the checkout page) would re-render on `_hasHydrated`, a flag it never
  // reads and which the persist lifecycle settles twice per load.
  // One atomic selector per value. A bare `useCart()` subscribes to the whole
  // store, which every `set()` replaces — so this hook (mounted by CartSheet
  // AND the checkout page) would re-render on `_hasHydrated`, a flag it never
  // reads and which the persist lifecycle settles twice per load.
  const items = useCart((s) => s.items);
  const removeItems = useCart((s) => s.removeItems);
  const patchItems = useCart((s) => s.patchItems);
  const validatingRef = useRef(false);

  const validateAndSyncCart = useCallback(async () => {
    if (items.length === 0) return;
    if (validatingRef.current) return;

    validatingRef.current = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch("/api/cart/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
        }),
        signal: controller.signal,
      });

      if (!res.ok) return;

      // ⚠ `as` is NOT validation — this only names the contract the route
      // PROMISES (lib/cart-validate-contract). What actually arrived is
      // untrusted network JSON, so every field below is still checked at
      // runtime; do not "simplify" the `??` fallbacks or the `typeof` guards
      // away on the strength of this cast.
      const data = (await res.json()) as CartValidateResponse;
      const invalid = data.invalid ?? [];
      const valid = data.valid ?? [];

      // Refresh `category` + `deliveryTiming` on every still-valid cart item
      // so a product re-categorised or re-timed in admin (e.g. Fresh →
      // Preserved, or Advance-only → Same-day) updates in place. This also
      // backfills `deliveryTiming` on older cart entries that predate the
      // field, which drives the earliest-preferred-date (pre-order) logic at
      // checkout. Skip items whose values haven't drifted.
      //
      // The `typeof === "string"` checks are the ONLY real validation on the
      // wire and must stay even though the contract declares both fields
      // required: a stale deploy, a proxy, or a hand-crafted response can omit
      // either, and writing `undefined` into the cart snapshot silently breaks
      // the delivery filter this refresh exists to keep honest.
      const updates = valid
        .map((v) => ({
          productId: v.productId,
          category: typeof v.category === "string" ? v.category : undefined,
          deliveryTiming: typeof v.deliveryTiming === "string" ? v.deliveryTiming : undefined,
        }))
        .filter((u) => {
          const cur = items.find((i) => i.productId === u.productId);
          if (!cur) return false;
          return (
            (u.category !== undefined && cur.category !== u.category) ||
            (u.deliveryTiming !== undefined && cur.deliveryTiming !== u.deliveryTiming)
          );
        });
      if (updates.length > 0) {
        patchItems(updates);
      }

      if (invalid.length > 0) {
        const ids = invalid.map((x) => x.productId);
        removeItems(ids);
        const count = ids.length;
        toast.info(
          count === 1
            ? "1 item has been removed from your cart—it is no longer available."
            : `${count} items have been removed from your cart—they are no longer available.`
        );
      }
    } catch {
      // Silent fail — validation is best-effort
    } finally {
      clearTimeout(timeout);
      validatingRef.current = false;
    }
  }, [items, removeItems, patchItems]);

  return { validateAndSyncCart };
}
